import React, { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { observer } from 'mobx-react-lite';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import styled from 'styled-components';

import {
    Colours,
    useToggle,
    IconChooser,
    PrimaryButton,
    timeoutAsPromise,
    PageBehaviourButton,
    AnimatedCardFlipContainer,
} from '@ratehub/base-ui';

import CreditCardShape from '../definitions/CreditCardShape';
import useApplyForCreditCardCallback from '../hooks/useApplyForCreditCardCallback';
import CreditCardPicker from './CreditCardPicker';
import CreditCardLayout from './CreditCardLayout';


function CreditCardFlipContainer({
    id,
    cardInfo,
    province,
    onSelect,
    onClear,
    recalculateHeightDelay = 750,
}) {
    const [ isShowingBack, toggleCard ] = useToggle(!!cardInfo);
    const [ cardId, setCardId ] = useState(cardInfo?.id);

    const applyForCreditCardCallback = useApplyForCreditCardCallback();

    function handleCCCallback(chosenCardInfo) {
        applyForCreditCardCallback({
            cardInfo: chosenCardInfo,
            componentName: 'CCSideBySide',
        });
    }

    async function handleSelectAndFlip() {
        await onSelect(cardId);
        toggleCard();
    }

    // saves the id of the chosen card, but does not act on it
    function handleChangePicker(value) {
        setCardId(value?.id);     // id of the chosen card
    }

    async function handleRemoveCard() {
        toggleCard();

        // short delay to avoid seeing the "card not found" message while flipping back
        await timeoutAsPromise(CARD_FLIP_DURATION / 2);
        onClear();

        // select all on the search box text when flipping the card back to the front
        const searchBox = document.getElementById(id);
        searchBox?.focus();
        searchBox?.select();
    }


    return (
        <AnimatedCardFlipContainer
            duration={CARD_FLIP_DURATION}
            isFlipped={isShowingBack}
            recalculateHeightDelay={recalculateHeightDelay}

            CardFront={(
                <PickYourCreditCard
                    id={id}
                    cardName={cardInfo?.name}
                    province={province}
                    isAddCardEnabled={!!cardId}
                    onSelectAndFlip={handleSelectAndFlip}
                    onChange={handleChangePicker}
                />
            )}

            CardBack={(
                <DisplayYourCreditCard
                    cardInfo={cardInfo}
                    onRemoveCard={handleRemoveCard}
                    onCTAClick={handleCCCallback}
                />
            )}
        />
    );
}

CreditCardFlipContainer.propTypes = {
    id: PropTypes.string.isRequired,
    cardInfo: PropTypes.shape(CreditCardShape),
    province: PropTypes.string.isRequired,
    onSelect: PropTypes.func.isRequired,
    onClear: PropTypes.func.isRequired,
    recalculateHeightDelay: PropTypes.number,
};


function PickYourCreditCard({
    id,
    cardName,
    province,
    onSelectAndFlip,
    isAddCardEnabled,
    onChange,
}) {
    return (
        <div className="rh-display-flex rh-flex-direction-column rh-p-2 rh-bg-stone-lightest">
            <label
                className="rh-title-2xs rh-mb-2 rh-mx-auto rh-text-align-center"
                htmlFor={id}
            >
                <FormattedMessage
                    id="edb.cc.flip-container.select.prompt"
                    defaultMessage="Name of credit card for comparison"
                />
            </label>

            <CreditCardPicker
                id={id}
                name={id}
                value={cardName}
                province={province}
                data-lpignore="true"    // for LastPass
                isRequired={false}
                onChange={onChange}
            />

            <PrimaryButton
                className="rh-mt-1"
                size="small"
                disabled={!isAddCardEnabled}
                onClick={onSelectAndFlip}
                data-name="picker-add-card"
            >
                <FormattedMessage
                    id="edb.cc.flip-container.add-card"
                    defaultMessage="Add card"
                />
            </PrimaryButton>
        </div>
    );
}

PickYourCreditCard.propTypes = {
    id: PropTypes.string.isRequired,
    cardName: PropTypes.string,
    province: PropTypes.string.isRequired,
    onSelectAndFlip: PropTypes.func.isRequired,
    isAddCardEnabled: PropTypes.bool.isRequired,
    onChange: PropTypes.func.isRequired,
};


function DisplayCreditCard({ cardInfo, onRemoveCard, onCTAClick, className }) {
    return (
        <div className={classNames(className, 'rh-position-relative')}>
            <span
                className={classNames('close-credit-card rh-position-absolute', {
                    // we want to optimize the close button placement with or without a gift offer
                    'has-gift-offer': cardInfo?.giftCardOffer,
                })}
            >
                <PageBehaviourButton
                    className="close-button-wrapper"        // so we can tab to the close button
                    onClick={onRemoveCard}
                    data-name="close-button-wrapper"
                >
                    <IconChooser
                        className="close-button"
                        iconKey="x"
                        size={cardInfo?.giftCardOffer
                            ? 'XS'
                            : 'S'
                        }
                        stroke={Colours.STONE_DARKEST}
                        fill={cardInfo?.giftCardOffer
                            ? Colours.COCONUT
                            : Colours.TRANSPARENT
                        }
                        includeOutline={false}
                        strokeWidth="2px"
                    />
                </PageBehaviourButton>
            </span>

            <Choose>
                <When condition={cardInfo}>
                    <CreditCardLayout
                        cardInfo={cardInfo}
                        productType={cardInfo.productType}
                        onClick={() => onCTAClick(cardInfo)}
                        isEqualizeHeight={true}
                    />
                </When>
                <Otherwise>
                    <p className="rh-text-s rh-py-5 rh-mx-3 leading-l rh-text-align-center">
                        <FormattedMessage
                            id="edb.cc.flip-container.missing-card"
                            defaultMessage="Sorry, something went wrong. We could not find that card."
                        />
                    </p>
                </Otherwise>
            </Choose>
        </div>
    );
}

DisplayCreditCard.propTypes = {
    cardInfo: PropTypes.shape(CreditCardShape),
    onRemoveCard: PropTypes.func.isRequired,
    onCTAClick: PropTypes.func.isRequired,
    className: PropTypes.string,
};


const CARD_FLIP_DURATION = 500;

const DisplayYourCreditCard = styled(DisplayCreditCard)`
    .close-credit-card {
        right: 0.25rem;
        top: 0.25rem;

        &.has-gift-offer {
            right: 0.3rem;
            top: 0.4rem;
        }

        .close-button:hover svg {
            stroke: ${Colours.BLUEBERRY};
        }

        .close-button:active svg {
            stroke: ${Colours.BLUEBERRY_DARK};
        }

        .close-button-wrapper:focus .close-button svg {
            stroke: ${Colours.BLUEBERRY_LIGHT};
        }
    }
`;


export default observer(CreditCardFlipContainer);
